import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Clock, Info } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export function TimeTrackingSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch current time tracking settings
  const { data: settings, isLoading } = useQuery<{
    globalEnabled: boolean;
    defaultProjectEnabled: boolean;
  }>({
    queryKey: ['/api/time-tracking/settings'],
  });

  // Local state for settings
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [defaultProjectEnabled, setDefaultProjectEnabled] = useState(true);

  // Update local state when settings are fetched
  useEffect(() => {
    if (settings) {
      setGlobalEnabled(settings.globalEnabled ?? true);
      setDefaultProjectEnabled(settings.defaultProjectEnabled ?? true);
    }
  }, [settings]);

  // Mutation to update settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: {
      globalEnabled?: boolean;
      defaultProjectEnabled?: boolean;
    }) => {
      return await apiRequest('PATCH', '/api/time-tracking/settings', updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/time-tracking/settings'] });
      toast({
        title: "Settings Updated",
        description: "Time tracking settings have been saved successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update time tracking settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateSettingsMutation.mutate({
      globalEnabled,
      defaultProjectEnabled,
    });
  };

  if (isLoading) {
    return <div className="text-gray-400">Loading time tracking settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-blue-400" />
        <h3 className="text-lg font-semibold text-white">Time Tracking Configuration</h3>
      </div>

      {/* Global Time Tracking Toggle */}
      <div className="space-y-4 p-4 border border-gray-600 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-white font-medium">Global Time Tracking</Label>
            <p className="text-sm text-gray-400">
              Master switch to enable/disable all time tracking functionality system-wide
            </p>
          </div>
          <Switch
            checked={globalEnabled}
            onCheckedChange={setGlobalEnabled}
            className="data-[state=checked]:bg-blue-600"
          />
        </div>

        {!globalEnabled && (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/50 rounded">
            <Info className="w-4 h-4 text-yellow-500 mt-0.5" />
            <p className="text-sm text-yellow-200">
              When disabled, all time tracking features will be hidden system-wide, including the Time button, 
              tracked times display, and time tracking popups. Individual project settings are preserved.
            </p>
          </div>
        )}
      </div>

      {/* Default for New Projects */}
      <div className="space-y-4 p-4 border border-gray-600 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-white font-medium">Default for New Projects</Label>
            <p className="text-sm text-gray-400">
              Whether time tracking should be enabled by default for newly created projects
            </p>
          </div>
          <Switch
            checked={defaultProjectEnabled}
            onCheckedChange={setDefaultProjectEnabled}
            disabled={!globalEnabled}
            className="data-[state=checked]:bg-blue-600"
          />
        </div>
      </div>



      {/* Per-Project Settings Info */}
      <div className="p-4 bg-gray-800/50 border border-gray-600 rounded-lg">
        <h4 className="text-white font-medium mb-2">Per-Project Time Tracking</h4>
        <p className="text-sm text-gray-400">
          Individual projects can have time tracking enabled or disabled in their project settings. 
          This allows fine-grained control over which projects use time tracking features.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          To change a project's time tracking setting, right-click on the project in the tree view 
          and select "Edit Project" from the context menu.
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={updateSettingsMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {updateSettingsMutation.isPending ? 'Saving...' : 'Save Time Tracking Settings'}
        </Button>
      </div>
    </div>
  );
}